import React, { useState } from 'react';
import { Search } from 'lucide-react';

const OperationalListControls = ({
  selectedYear,
  availableYears,
  search,
  moduleName = 'Invoices',
  searchPlaceholder,
  showResultCount = true,
  isSearchActive,
  pagination,
  onYearChange,
  onSearchChange,
  onPageChange,
  theme = {}
}) => {
  const [showOtherYear, setShowOtherYear] = useState(false);
  const [otherYear, setOtherYear] = useState('');
  const [otherYearError, setOtherYearError] = useState('');

  const moduleLabel = String(moduleName || 'Invoices');
  const moduleSingular = moduleLabel.replace(/s$/i, '').toLowerCase();
  const years = Array.from(new Set([
    Number(selectedYear),
    new Date().getFullYear(),
    ...(Array.isArray(availableYears) ? availableYears.map(Number) : [])
  ]))
    .filter((year) => Number.isInteger(year))
    .sort((a, b) => b - a);

  const page = Number(pagination?.page || 1);
  const totalPages = Number(pagination?.total_pages || 1);
  const total = Number(pagination?.total || 0);
  const resultModuleLabel = total === 1 ? moduleSingular : moduleLabel.toLowerCase();
  const colors = {
    background: '#0a1128',
    border: '#E8761D',
    accent: '#FFA500',
    text: 'white',
    inputBackground: 'white',
    inputText: 'black',
    icon: '#0a1128',
    buttonBackground: '#FFC107',
    buttonText: '#000',
    buttonHover: '#FFA500',
    divider: 'rgba(255, 165, 0, 0.45)',
    error: '#ffb4b4',
    ...theme
  };
  const otherYearButtonBorder = theme.otherYearButtonBorder || colors.border;
  const otherYearButtonHover = theme.otherYearButtonHover || colors.buttonHover;

  const submitOtherYear = (event) => {
    event.preventDefault();
    const value = String(otherYear || '').trim();
    if (!/^\d{4}$/.test(value)) {
      setOtherYearError('Enter a four-digit year.');
      return;
    }
    setOtherYearError('');
    setShowOtherYear(false);
    onYearChange(Number(value));
  };

  return (
    <div style={{ marginBottom: '16px', border: `3px solid ${colors.border}`, padding: '18px', backgroundColor: colors.background, boxSizing: 'border-box', '--operational-divider': colors.divider, '--operational-button': colors.buttonBackground, '--operational-button-text': colors.buttonText, '--operational-button-border': colors.border, '--operational-button-hover': colors.buttonHover }}>
      <style>{`
        .operational-list-controls-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: end;
        }
        .operational-list-control-group {
          display: grid;
          gap: 7px;
          min-width: 170px;
          padding-right: 16px;
          border-right: 2px solid var(--operational-divider);
        }
        .operational-list-search-group {
          min-width: 280px;
          flex: 1 1 280px;
          padding-right: 0;
          border-right: 0;
        }
        .operational-list-btn {
          background: var(--operational-button);
          color: var(--operational-button-text);
          border: 3px solid var(--operational-button-border);
          font-weight: 900;
          cursor: pointer;
          transition: filter 120ms ease, transform 120ms ease, background-color 120ms ease;
        }
        .operational-list-btn:hover:not(:disabled) {
          background: var(--operational-button-hover);
          filter: brightness(1.03);
        }
        .operational-list-btn:active:not(:disabled) {
          transform: translateY(1px);
        }
        .operational-list-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        @media (max-width: 760px) {
          .operational-list-controls-row {
            justify-content: center;
          }
          .operational-list-control-group {
            border-right: 0;
            padding-right: 0;
            justify-items: center;
            text-align: center;
          }
          .operational-list-search-group {
            flex-basis: 100%;
            min-width: 0;
          }
          .operational-list-search-group > label {
            width: 100%;
          }
          .operational-list-search-input {
            padding: 10px 10px 10px 42px !important;
          }
          .operational-list-other-year-group {
            justify-content: center;
          }
          .operational-list-status {
            text-align: center;
            font-size: 15px;
          }
        }
      `}</style>

      <div className="operational-list-controls-row">
        <div className="operational-list-control-group">
          <label style={{ display: 'grid', gap: '7px', fontWeight: 900, color: colors.accent, letterSpacing: '0.5px' }}>
            VIEW {moduleLabel.toUpperCase()}
            <select
              value={showOtherYear ? '__other_year__' : selectedYear}
              onChange={(event) => {
                if (event.target.value === '__other_year__') {
                  setShowOtherYear(true);
                  setOtherYear('');
                  setOtherYearError('');
                  return;
                }
                setShowOtherYear(false);
                onYearChange(Number(event.target.value));
              }}
              style={{ padding: '11px', border: `3px solid ${colors.border}`, backgroundColor: colors.inputBackground, color: colors.inputText, fontWeight: 900, fontSize: '16px' }}
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
              <option value="__other_year__">OTHER YEAR…</option>
            </select>
          </label>
        </div>

        {showOtherYear && (
          <form onSubmit={submitOtherYear} className="operational-list-control-group operational-list-other-year-group" style={{ display: 'flex', gap: '9px', alignItems: 'end', flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: '7px', fontWeight: 900, color: colors.accent, letterSpacing: '0.5px' }}>
              OTHER YEAR
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength="4"
                value={otherYear}
                onChange={(event) => {
                  setOtherYear(event.target.value);
                  setOtherYearError('');
                }}
                style={{ width: '108px', padding: '11px', border: `3px solid ${colors.border}`, backgroundColor: colors.inputBackground, color: colors.inputText, fontWeight: 900, fontSize: '16px', boxSizing: 'border-box' }}
              />
            </label>
            <button
              type="submit"
              className="operational-list-btn"
              style={{ padding: '11px 16px', letterSpacing: '0.3px', '--operational-button-border': otherYearButtonBorder, '--operational-button-hover': otherYearButtonHover }}
            >
              VIEW YEAR
            </button>
            {otherYearError && (
              <span style={{ color: colors.error, fontWeight: 900 }}>{otherYearError}</span>
            )}
          </form>
        )}

        <div className="operational-list-control-group operational-list-search-group">
          <label style={{ display: 'grid', gap: '7px', fontWeight: 900, color: colors.accent, letterSpacing: '0.5px' }}>
            SEARCH ALL {moduleLabel.toUpperCase()}
            <span style={{ position: 'relative', display: 'block' }}>
              <Search
                size={20}
                strokeWidth={3}
                aria-hidden="true"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.icon, pointerEvents: 'none' }}
              />
              <input
                className="operational-list-search-input"
                type="text"
                placeholder={searchPlaceholder || `Customer, contact, ${moduleSingular} #, phone, or email`}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                style={{ width: '100%', padding: '11px 11px 11px 42px', border: `3px solid ${colors.border}`, backgroundColor: colors.inputBackground, color: colors.inputText, fontWeight: 700, fontSize: '16px', boxSizing: 'border-box' }}
              />
            </span>
          </label>
        </div>
      </div>

      <div className="operational-list-status" style={{ marginTop: '16px', color: colors.text, fontWeight: 900, letterSpacing: '0.2px' }}>
        {showResultCount
          ? (isSearchActive
            ? <>Searching all years — <span style={{ color: colors.accent }}>{total} {resultModuleLabel}</span> found</>
            : <>Viewing {selectedYear} {moduleLabel} — <span style={{ color: colors.accent }}>{total} {resultModuleLabel}</span> found</>)
          : (isSearchActive ? `Searching all ${moduleLabel}` : `Viewing ${selectedYear} ${moduleLabel}`)}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px', color: colors.text }}>
          <button
            type="button"
            className="operational-list-btn"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            style={{ padding: '10px 14px' }}
          >
            ← PREVIOUS
          </button>
          <span style={{ fontWeight: 900 }}>PAGE {page} OF {totalPages}</span>
          <button
            type="button"
            className="operational-list-btn"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{ padding: '10px 14px' }}
          >
            NEXT →
          </button>
        </div>
      )}
    </div>
  );
};

export default OperationalListControls;
